import { SelectPopover as AriaKitSelectPopover, SelectState as AriaKitSelectState } from "ariakit";
import React from "react";
import styled from "styled-components";
import { useEvent } from "../hooks/useEvent";
import { useHandleResize } from "../hooks/useHandleResize";
import { PropOptionRenderItem } from "../PropX";
import { style } from "../style";
import { SelectList } from "./SelectList";
import { usePrecalculatedOuterItemHeights } from "./usePrecalculatedOuterItemHeights";

const MEDIUM_WIDTH = "200px";
const LARGE_WIDTH = "400px";
const AUTO_WIDTH = "auto";

const PopoverContainer = styled.div<{ $visible: boolean }>`
  visibility: ${(props) => (props.$visible ? "visible" : "hidden")};
`;

const Popover = styled(AriaKitSelectPopover)<{ $width: string }>`
  display: block;
  width: ${(props) => props.$width};
  min-width: ${MEDIUM_WIDTH};

  background: ${style.colors.white};
  border: ${style.vars.borderWidth.thin} solid ${style.colors.blue.dark};
  border-bottom-left-radius: ${style.vars.borderRadius.base};
  border-bottom-right-radius: ${style.vars.borderRadius.base};

  z-index: ${style.vars.zIndex.top};
`;

const ListHeaderContainer = styled.div`
  border-bottom: ${style.vars.borderWidth.thin} solid ${style.colors.grey.light};
`;

const ListFooterContainer = styled.div`
  border-top: ${style.vars.borderWidth.thin} solid ${style.colors.grey.light};
`;

type Props = {
  state: AriaKitSelectState;
  width: "medium" | "large" | "auto" | (() => string);
  id: string;
  closeOnEscape: boolean;
  autoFocusOnShow: boolean;
  autoFocusOnHide: boolean;
  items: PropOptionRenderItem[];
  listHeader?: React.ReactNode;
  listFooter?: React.ReactNode;
  mono?: boolean;
  compact?: boolean;
  UNSAFE_repositionKey?: string | number;
  UNSAFE_forceHide?: boolean;
  UNSAFE_overscanCount?: number;
  UNSAFE_precalculateItemHeights?: boolean;
  "data-testid"(value: string): string;
};

export const SelectPopover = (props: Props): JSX.Element => {
  const {
    items,
    state,
    width,
    id,
    closeOnEscape,
    autoFocusOnShow,
    autoFocusOnHide,
    UNSAFE_repositionKey,
    listHeader = null,
    listFooter = null,
    compact = false,
    mono = false,
    UNSAFE_forceHide = false,
    UNSAFE_precalculateItemHeights = false,
    UNSAFE_overscanCount = 10,
  } = props;

  const [menuWidth, recalculateMenuWidth] = React.useReducer(() => {
    switch (width) {
      case "medium":
        return MEDIUM_WIDTH;
      case "large":
        return LARGE_WIDTH;
      case "auto":
        return AUTO_WIDTH;
      default: {
        return width();
      }
    }
  }, "auto");

  React.useLayoutEffect(recalculateMenuWidth, [width]);
  useHandleResize(recalculateMenuWidth);

  React.useLayoutEffect(() => {
    /**
     * If we open the menu and the width is a function,
     * we need to recalculate the width of the menu to ensure that it is correct.
     */
    if (state.open && typeof width === "function") {
      recalculateMenuWidth();
    }
  }, [state.open]);

  React.useEffect(() => {
    /**
     * This is a bit of a hack, but the SelectPopover is positioned absolutely relative
     * to the state.anchorRef. It does not recalculate its position when the state.anchorRef is resized in
     * some circumstances (I'm not sure why), but it does when a scroll event happens. Therefore, we can
     * trigger a scroll event (with no data) to force the library to reclaculate the position.
     */
    window.dispatchEvent(new Event("scroll"));
  }, [UNSAFE_repositionKey]);

  const onKeyDown = useEvent<React.KeyboardEventHandler<HTMLDivElement>>((ev) => {
    /**
     * Force the menu to stay open if `closeOnEscape` is false when pressing the escape key.
     * This overrides the default behavior of the menu, but is necessary for some of the radio controls
     * where the menu is opened when a value has never been selected. That is because pressing
     * escape focuses the input, which opens the menu again.
     */
    if (ev.key === "Escape" && !closeOnEscape) {
      ev.preventDefault();
    }
  });

  const precalculatedItemHeights = usePrecalculatedOuterItemHeights({
    items,
    props: { state, compact, mono },
    elementWidth: menuWidth,
    elementMinWidth: MEDIUM_WIDTH,
    skip: !UNSAFE_precalculateItemHeights,
  });

  if (precalculatedItemHeights.status === "pending" || precalculatedItemHeights.status === "computing") {
    return precalculatedItemHeights.elementToRender;
  }

  return (
    <PopoverContainer $visible={!UNSAFE_forceHide && items.length > 0}>
      <Popover
        state={state}
        id={id}
        $width={menuWidth}
        onKeyDown={onKeyDown}
        autoFocusOnShow={autoFocusOnShow}
        autoFocusOnHide={autoFocusOnHide}
      >
        {listHeader && <ListHeaderContainer tabIndex={-1}>{listHeader}</ListHeaderContainer>}
        <SelectList
          key={precalculatedItemHeights.key}
          state={state}
          items={items}
          mono={mono}
          compact={compact}
          overscanCount={UNSAFE_overscanCount}
          data-testid={props["data-testid"]}
          UNSAFE_precalculatedItemHeights={precalculatedItemHeights.heights}
        />
        {listFooter && <ListFooterContainer tabIndex={-1}>{listFooter}</ListFooterContainer>}
      </Popover>
    </PopoverContainer>
  );
};

SelectPopover.displayName = "SelectPopover";
